import {get, isEmpty, isNil} from 'lodash'
import {jwtDecode} from "jwt-decode"
import {normaliseToArray} from 'helpers/component'
import {isDormantContact, TYPE} from 'helpers/contacts'
import {SubscriptionBillingPeriod} from 'helpers/subscriptions'
import {mainPackages, partCodes} from 'helpers/plans'
import {
  AccountType,
  AUTHORISATION_LEVEL,
  featureIsAllocatable,
  features,
  featureToggles,
  permissions, USER_LEVEL,
} from 'helpers/constants'
import {isPricingCalculatorsActive} from "helpers/pricingCalculator"

export const isExpired = (user) => (user && user.authorisationLevel === AUTHORISATION_LEVEL.ACCOUNT_EXPIRED)

export const isTfaSetupMandatory = (user) =>
  (user && user.authorisationLevel === AUTHORISATION_LEVEL.TFA_SETUP_REQUIRED)

export const shouldShowEnableTfa = (user) => {
  const authLevel = get(user, 'authorisationLevel', AUTHORISATION_LEVEL.FULLY_AUTHORISED)
  const tfaEnabled = get(user, 'authSettings.totpEnabled', false)
  const tfaPromptDismissed = get(user, 'authSettings.tfaEnablePromptDismissed', false)
  return !tfaEnabled && (authLevel === AUTHORISATION_LEVEL.TFA_SETUP_REQUIRED || !tfaPromptDismissed)
}

/**
 * Checks if the user @param {*} user has the specified @param {*} permission.
 * @returns {boolean}
 */
export const userHasPermission = (user, permission) => normaliseToArray(permission).reduce((ret, p) => {
  return ret && user && user.permissions && user.permissions.indexOf(p) > -1
}, true)

/**
 * Checks if the user @param {*} user has the specified @param {*} feature.
 * @returns {boolean}
 */
export const userHasFeature = (user, feature) => normaliseToArray(feature).reduce((ret, p) => {
  return ret && user && user.features && user.features.indexOf(p) > -1
}, true)

/**
 * Checks if the account specified by user @param {*} user has the specified @param {*} feature.
 * @returns {boolean}
 */
export const accountHasFeature = (user, feature) => normaliseToArray(feature).reduce((ret, p) => {
  return ret && user && user.accountFeatures && user.accountFeatures[p] > 0
}, true)

/**
 * Checks if the account specified by user @param {*} user has the specified @param {*} calculator.
 * @returns {boolean}
 */
export const accountHasCalculator = (user, calculatorName) => normaliseToArray(calculatorName).reduce((ret, p) => {
  return ret && user && user.calculators && user.calculators.includes(p)
}, true)

export const featureToggleIsOn = (user, featureToggle) => {
  return !!(user?.featureToggles || []).find(ft => ft === featureToggle)
}

export const userHasMainPackage = (user) => {
  const packages = get(user, 'packages', [])
  return packages.some(pkg => mainPackages.includes(pkg.partCode))
}

export const userIsPodOnly = (user) => {
  return userHasFeature(user, features.POD_LICENCE) && !userHasFeature(user, features.MASTER_LICENCE)
}

/** Checks if the account or @param {*} user for the specified @param {*} feature.
 * @returns {boolean} True if either the user has the feature or the account *doesn't* have the
 * feature and an upgrade is available (for the purposes of advertising the feature)
 */
export const shouldDisplayOption = (user, feature) => {
  return accountHasFeature(user, feature)
    ? userHasFeature(user, feature) || !featureIsAllocatable(feature)
    : userHasMainPackage(user)
}

/** Checks if the user is syncing with the given version of Pro
 * @returns {boolean} True if the user is syncing with at least the specified version,
 * or defaultIfNotSyncing if not syncing
 */
export const checkProVersion = (user, version, defaultIfNotSyncing = true) => {
  if (!user.proSync) return defaultIfNotSyncing
  return (user.proIntVersion >= version)
}

/** Returns the number of licences of the given type that the user's Pro system actually has
 * allocated to users/seats.
 * Returns NULL if this number cannot be determined.
 * @param user
 * @param licence
 * @returns {null|number}
 */
export const getProLicencesUsed = (user, licence) => {
  if (!user.proSync) return 0
  if (!user.proUsedLicences) return null
  return user.proUsedLicences[licence]
}

export const PRO_VERSION = {
  v4: 4000000000,
  v5: 5000000000,
  v5p1: 5001000000,
  v5p1p20: 5001020000,
  v5p1p30: 5001030000,
  v5p1p35: 5001035000,
  v5p1p40: 5001040000,
  v5p1p45: 5001045000,
  v5p1p50: 5001050000,
  v5p1p60: 5001060000,
  v5p1p65: 5001065000,
  v5p1p80: 5001080000,
  v5p1p86: 5001086000,
  v5p2: 5002000000,
  v5p2p2: 5002002000,
  v5p3p1: 5003001000,
  v5p3p2: 5003002000,
  v5p3p3: 5003003000,
  v5p3p4: 5003004000,
  v6p2p3: 6002003000,
  v6p4p4: 6004004000,
}

export const isSignUpAccountType = (user) => {
  return get(user, 'accountTypeId') === AccountType.SIGN_UP
}

export const isTrialAccountType = (user) => {
  return get(user, 'accountTypeId') === AccountType.TRIAL
}

export const isCustomerAccountType = (user) => {
  return get(user, 'accountTypeId') === AccountType.CUSTOMER
}

export const isInternalAccountType = (user) => {
  return get(user, 'accountTypeId') === AccountType.INTERNAL
}

export const isBetaAccountType = (user) => {
  return get(user, 'accountTypeId') === AccountType.BETA
}

export const isBinAccountType = (user) => {
  return get(user, 'accountTypeId') === AccountType.BIN
}

export const isFreeCustomerAccountType = (user) => {
  return get(user, 'accountTypeId') === AccountType.FREE_CUSTOMER
}

export const isManagementUserAccountType = (user) => {
  return (
    get(user, 'accountTypeId') === AccountType.MANAGEMENT
  )
}

export const isUnknownAccountType = (user) => {
  return get(user, 'accountTypeId') === AccountType.UNKNOWN
}

export const getUserName = (user) => {
  return get(user, 'name')
}

export const getHomeCompanyName = (user) => {
  return get(user, 'homeCompanyName')
}

export const userHasSales = (user) => {
  return userHasFeature(user, features.SALES_LICENCE)
}

export const accountHasSales = (user) => {
  return accountHasFeature(user, features.SALES_LICENCE)
}

export const getExistingUserError = (internalUsers, externalUsers, contact) => {
  if (isNil(contact))
    return null

  const contactId = get(contact, 'id', null)
  const email = get(contact, 'email', null)
  const users = internalUsers.concat(externalUsers)
  const userContactIds = users.map(user => user.contactId)


  if (userContactIds.includes(contactId))
    return 'There is already a user for this contact'

  if (isEmpty(email))
    return 'There is no valid email address for this contact'

  const userEmails = users
    .map(user => user.email)
    .filter(email => !isEmpty(email))
    .map(email => email.toLowerCase())

  if (userEmails.includes(email.toLowerCase()))
    return 'There is already a user with this email address'

  return null
}

export const canAddExternalUser = (user) => {
  return accountHasFeature(user, features.POD_LICENCE) && userHasPermission(user, permissions.MANAGE_LOGINS)
}

export const getExternalUserLicences = (licences) => {
  return licences.filter(licence => licence.id === features.POD_LICENCE)
}

export const getAllocatableExternalUserPackages = (userPackages) => {
  return userPackages.filter(userPackage => userPackage.partCode === partCodes.pod && userPackage.qty > 0)
}

export const getDefaultExternalUserPackagesAndLicence = (userPackages) => {
  const userPackage = userPackages.find(userPackage =>
    userPackage.partCode === partCodes.pod
    && userPackage.qty > 0
    && userPackage.qty !== userPackage.used)

  if (userPackage) {
    const licences = get(userPackage, 'licences', [])
    return {
      userPackages: [{
        id: userPackage.id,
        partCode: userPackage.partCode,
        licences: licences.map(licence => licence.licenceId),
      }],
      licences: licences
        .filter(licence => licence.licenceId === features.POD_LICENCE)
        .map(licence => licence.licenceId),
    }
  }
  return {
    userPackages: [],
    licences: [],
  }
}

export const getWarningMessage = (type, loginRemaining) => {
  return (type === 'internalUser' && loginRemaining === 0)
    ? "Need more users? "
    : (type === 'POD' && loginRemaining === 0)
      ? 'For additional POD licenses, call'
      : ''
}

export const getRemainingLogins = (licences) => {
  return licences
    .filter(licence => licence.id !== features.MASTER_LICENCE && licence.id !== features.POD_LICENCE)
    .reduce((sumOfRemainingLicences, {remaining}) => sumOfRemainingLicences + remaining, 0)
}

export const getRemainingPodLogins = (licences) => {
  return licences
    .filter(licence => licence.id === features.POD_LICENCE)
    .reduce((sumOfRemainingLicences, {remaining}) => sumOfRemainingLicences + remaining, 0)
}

export const isContactDormant = (selectedContact) => {
  const type = get(selectedContact, 'type', TYPE.general)
  return isDormantContact(type)
}

export const isAccountSubscriptionYearly = (user) => {
  return get(user, 'billingRecurType') === SubscriptionBillingPeriod.YEARLY
}

export const getSearchedUsers = (searchValue, users) => {
  if (isEmpty(searchValue)) return users

  const search = searchValue.toLowerCase()

  return users.filter(user => {
    return user.name.toLowerCase().includes(search) || user.email.toLowerCase().includes(search)
  })
}

export const updateUsersFromContacts = (users, contacts) => {
  return users.map(user => {
    const contact = contacts[user.contactId]
    return isNil(contact)
      ? user
      : {
        ...user,
        email: contact.email,
        forename: contact.forename,
        surname: contact.surname,
        isValidated: contact.isValidated,
      }
  })
}

export const getSelectedUserId = (selectedUser) => {
  const selectedUserUserId = get(selectedUser, 'userId', null)
  const selectedUserId = get(selectedUser, 'id', null)

  return isNil(selectedUserUserId) ? selectedUserId : selectedUserUserId
}

export const isUserTFAEnabled = (selectedUser, authUsersSettings) => {
  if (isNil(authUsersSettings) || isNil(selectedUser)) return false

  const selectedUserId = getSelectedUserId(selectedUser)
  const userSettings = authUsersSettings.find(us => us.userId === selectedUserId)
  if (isNil(userSettings)) return false

  return get(userSettings, 'totpEnabled', false)
}

export const isUserRequiresTFASetup = (selectedUser, authUsersSettings) => {
  if (isNil(authUsersSettings) || isNil(selectedUser)) return false

  const selectedUserId = getSelectedUserId(selectedUser)
  const userSettings = authUsersSettings.find(us => us.userId === selectedUserId)
  if (isNil(userSettings)) return false

  const totpEnabled = get(userSettings, 'totpEnabled', false)
  const tfaOptional = get(userSettings, 'tfaOptional', true)
  return !totpEnabled && !tfaOptional
}

export const isPrintTemplateEditorEnabled = (user) => {
  return featureToggleIsOn(user, featureToggles.PRINT_TEMPLATE_EDITOR) || isSupportUser(user)
}

export const isGoogleDriveFeatureToggleEnabled = (user) => {
  return featureToggleIsOn(user, featureToggles.GOOGLE_DRIVE)
}

export const isPricingCalculatorFeatureToggleEnabled = (user) => {
  return featureToggleIsOn(user, featureToggles.PRICING_CALCULATOR)
}

export const isPricingCalculatorPriceListEnabled = (user) => {
  return isPricingCalculatorFeatureToggleEnabled(user)
    && featureToggleIsOn(user, featureToggles.PRICING_CALCULATOR_PRICE_LIST)
}

export const canManagePricingCalculatorResources = (user) => {
  return isPricingCalculatorsActive(user)
    && userHasPermission(user, permissions.MANAGE_PRICING_CALCULATOR_RESOURCES)
}

export const isQuotationAutomaticRemindersFeatureToggleEnabled = (user) => {
  return featureToggleIsOn(user, featureToggles.QUOTATION_AUTOMATIC_REMINDERS)
}

export const isBulkInvoicesFeatureToggleEnabled = (user) => {
  return featureToggleIsOn(user, featureToggles.BULK_INVOICES)
}

export const hasBulkInvoicingLicence = (user) => {
  return accountHasFeature(user, features.BULK_INVOICING)
}

export const canBulkCreateInvoices = (user) => {
  return userHasPermission(user, permissions.BULK_CREATE_INVOICES)
}

export const canShowBulkInvoiceFeature = (user) => {
  if (!isBulkInvoicesFeatureToggleEnabled(user)) return false
  return !hasBulkInvoicingLicence(user) || canBulkCreateInvoices(user)
}

export const isTokenActive = (token) => {
  if (isNil(token) || isEmpty(token))
    return false

  const decoded = jwtDecode(token)
  const expiry = get(decoded, 'exp', null)
  return expiry === null || expiry * 1000 > Date.now()
}

export const sessionIsAuthenticationLevel = (response, level) => {
  return get(response, 'data.authorisationLevel') === level
}

export const isTfaEnabledAndMandatory = (authSettings, isTfaEnabled) => {
  const isTFAOptional = get(authSettings, 'twoFactorAuthOptional', true)
  return (!isTFAOptional && isTfaEnabled)
}


export const isTwoFactorEnabledForAnyUser = (authUserSettings) => {
  return authUserSettings.some(userSettings => get(userSettings, 'totpEnabled', false))
}

export const isJobCardDialogReadOnly = (user) => {
  return !userHasPermission(user, permissions.MANAGE_JOB_CARDS) ||
    !userHasFeature(user, features.PRODUCTION_LICENCE)
}

export const parseUserName = (fullName) => {
  const nameParts = (fullName || '').trim().split(' ')
  const forename = nameParts[0] || ''
  const surname = nameParts.slice(1).join(' ') || ''
  return {forename, surname}
}

export const getAccountTypeName = (accountTypeId) => {
  const accountTypes = {
    [AccountType.SIGN_UP]: 'Sign Up',
    [AccountType.TRIAL]: 'Trial',
    [AccountType.CUSTOMER]: 'Customer',
    [AccountType.INTERNAL]: 'Internal',
    [AccountType.BETA]: 'Beta',
    [AccountType.BIN]: 'Bin',
    [AccountType.FREE_CUSTOMER]: 'Free Customer',
    [AccountType.MANAGEMENT]: 'Management',
    [AccountType.UNKNOWN]: 'Unknown',
  }
  return accountTypes[accountTypeId] || 'Unknown'
}

export const isUserReadOnly = (user, currentUser) => {
  return (
    user.level === USER_LEVEL.SUPPORT &&
    currentUser.level !== USER_LEVEL.SUPPORT
  )
}

export const isSupportUser = (user) => {
  return user?.level === USER_LEVEL.SUPPORT
}
